import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';

/**
 * STITCH-2 drawer shell primitive -- generalizes the sla-drawer idiom
 * (scrim + right slide-over aside) so every quick view shares one shell:
 * overlay elevation (3rd shadow step), eyebrow + title head, scrollable
 * body, sticky footer slot for the role-aware action strip. Close on scrim
 * click, the head button, or Escape. Presentation-only: content and any
 * authority decisions belong to the caller.
 *
 * V7-P4A corrective (founder review on PR #263): OPTIONAL dismissal
 * controls so a composing dialog owns every focusable affordance the shell
 * contributes -- ConfirmDialog hides the head Close button (its contract is
 * EXACTLY two actions) and structurally blocks scrim/Escape dismissal while
 * busy (never a visibly-interactive no-op). Every control DEFAULTS to
 * today's behavior, so existing consumers render byte-identically.
 */
export interface DrawerShellProps {
  readonly eyebrow?: string;
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly testId?: string;
  /** Hide the head Close button (hosts that carry their OWN complete
   *  action strip, e.g. ConfirmDialog). Default false = button renders. */
  readonly hideCloseButton?: boolean;
  /** Render the head Close button disabled (busy consumers that keep the
   *  button visible). Default false. */
  readonly closeDisabled?: boolean;
  /** Scrim click dismisses. Default true = today's behavior. */
  readonly closeOnScrim?: boolean;
  /** Escape dismisses. Default true = today's behavior. */
  readonly closeOnEscape?: boolean;
}

export interface DrawerDismissal {
  readonly showCloseButton: boolean;
  readonly closeButtonDisabled: boolean;
  readonly scrimCloses: boolean;
  readonly escapeCloses: boolean;
}

/**
 * Resolve the shell's dismissal affordances from the optional controls.
 * Pure -- exported for deterministic tests (no jsdom in this repo); the
 * component consumes exactly this resolution.
 */
export function resolveDrawerDismissal({
  hideCloseButton = false,
  closeDisabled = false,
  closeOnScrim = true,
  closeOnEscape = true,
}: Pick<
  DrawerShellProps,
  'hideCloseButton' | 'closeDisabled' | 'closeOnScrim' | 'closeOnEscape'
>): DrawerDismissal {
  return {
    showCloseButton: !hideCloseButton,
    closeButtonDisabled: closeDisabled,
    scrimCloses: closeOnScrim,
    escapeCloses: closeOnEscape,
  };
}

export default function DrawerShell({
  eyebrow,
  title,
  onClose,
  children,
  footer,
  testId,
  hideCloseButton,
  closeDisabled,
  closeOnScrim,
  closeOnEscape,
}: DrawerShellProps) {
  const dismissal = resolveDrawerDismissal({
    hideCloseButton,
    closeDisabled,
    closeOnScrim,
    closeOnEscape,
  });
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape' && dismissal.escapeCloses) onClose();
  };
  // X2 (ENTERPRISE-CRM-EXPERIENCE-ELEVATION-V1, SAFE tier): seed focus INTO
  // the dialog on open so Escape works immediately (the aside's keydown
  // only fires with focus inside). Initial-focus seeding only -- no focus
  // trap, no tab-cycle rewrite; SSR renders are unaffected (no effects).
  const asideRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = asideRef.current;
    if (el !== null && !el.contains(document.activeElement)) {
      el.focus();
    }
  }, []);
  return (
    <>
      <div
        className="ac-drawer-scrim"
        onClick={dismissal.scrimCloses ? onClose : undefined}
        data-testid={testId ? `${testId}-scrim` : undefined}
      />
      <aside
        className="ac-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onKeyDown={onKeyDown}
        data-testid={testId}
        ref={asideRef}
        tabIndex={-1}
      >
        <div className="ac-drawer__head">
          <div>
            {eyebrow ? <p className="ac-drawer__eyebrow">{eyebrow}</p> : null}
            <h2 className="ac-drawer__title">{title}</h2>
          </div>
          {dismissal.showCloseButton ? (
            <Button
              variant="ghost"
              size="sm"
              type="button"
              className="ac-btn ac-btn--ghost ac-btn--sm"
              onClick={onClose}
              disabled={dismissal.closeButtonDisabled}
              aria-label="Close"
            >
              Close
            </Button>
          ) : null}
        </div>
        <div className="ac-drawer__body">{children}</div>
        {footer ? <div className="ac-drawer__footer">{footer}</div> : null}
      </aside>
    </>
  );
}
